import { round } from 'lodash'
import {
  Box,
  Divider,
  Grid,
  InputProps,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Control, FieldErrors } from 'react-hook-form'
import { useReactiveVar } from '@apollo/client'
import { projectVar } from '../../../../apollo/reactive-vars'
import { ScheduleFragment } from '../../../../generated/graphql'
import { FormValues } from '../form-values'
import { FormGroup } from '../../../form/FormGroup'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { validators } from '../../../../validation/validators'
import {
  areaUnit,
  energyCapacityUnit,
  powerPerAreaUnit,
  temperatureUnit,
} from '../../../../util/formatters'
import { theme } from '../../../../theme'
import { calculateHourSerieFactorsSum } from '../../../../schedule/hour-series-factors-calculator'

const useStyles = makeStyles({
  centeredSelectList: {
    justifyContent: 'center',
  },
})

const centeredTextField: InputProps = {
  margin: 'dense',
  inputProps: { style: { textAlign: 'center' } },
}

const centeredSelect: InputProps = {
  margin: 'dense',
  style: { textAlign: 'center' },
}

type Props = {
  schedules: ScheduleFragment[]
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
}

export function HeatLoadForm({
  schedules,
  control,
  errors,
}: Props): React.ReactElement {
  const applianceEleRef = useRef<number | undefined>(0)
  const lightingEleRef = useRef<number | undefined>(0)

  const occupantExploitRef = useRef<number | undefined>(0)
  const appliancExploitRef = useRef<number | undefined>(0)
  const lightingExploitRef = useRef<number | undefined>(0)

  const [occupantLoad, setOccupantLoad] = useState<number | undefined>(0)
  const [applianceLoad, setApplianceLoad] = useState<number | undefined>(0)
  const [lightingLoad, setLightingLoad] = useState<number | undefined>(0)

  const [occupantFactor, setOccupantFactor] = useState<number | undefined>(0)
  const [applianceFactor, setApplianceFactor] = useState<number | undefined>(0)
  const [lightingFactor, setLightingFactor] = useState<number | undefined>(0)

  const { t } = useTranslation()

  const classes = useStyles()

  const project = useReactiveVar(projectVar)

  const occupantScheduleId = control.watchInternal(
    'parameters.heatLoad.occupantScheduleId',
  ) as string | undefined

  const applianceScheduleId = control.watchInternal(
    'parameters.heatLoad.applianceScheduleId',
  ) as string | undefined

  const lightingScheduleId = control.watchInternal(
    'parameters.heatLoad.lightingScheduleId',
  ) as string | undefined

  // TODO: Cache used schedule sums -> not necessary to calculate on every change event
  const calculateScheduleSum = (scheduleId: string | undefined) => {
    if (scheduleId === undefined || project?.calendarYear === undefined)
      return undefined

    const schedule = schedules.find((s) => s.id === scheduleId)
    if (schedule === undefined) return undefined

    return calculateHourSerieFactorsSum(
      schedule,
      project.calendarYear as number,
    )
  }

  const calcTotalHeatLoad = () => {
    if (
      occupantLoad !== undefined &&
      applianceLoad !== undefined &&
      lightingLoad !== undefined
    )
      return round(occupantLoad + applianceLoad + lightingLoad, 1)

    return undefined
  }

  const calcTotalElectricity = () => {
    if (
      applianceEleRef.current !== undefined &&
      lightingEleRef.current !== undefined
    )
      return round(applianceEleRef.current + lightingEleRef.current, 1)

    return undefined
  }

  const calcApplianceElectricity = () => {
    const amountEle = calculateScheduleSum(applianceScheduleId)
    if (applianceLoad !== undefined && amountEle !== undefined) {
      applianceEleRef.current = round((applianceLoad * amountEle) / 1000, 1)
      return applianceEleRef.current
    }

    return undefined
  }

  const calcLightningElectricity = () => {
    const amountEle = calculateScheduleSum(lightingScheduleId)
    if (lightingLoad !== undefined && amountEle !== undefined) {
      lightingEleRef.current = round((lightingLoad * amountEle) / 1000, 1)
      return lightingEleRef.current
    }

    return undefined
  }

  const calcTotalExploitableHeatload = () => {
    if (
      occupantExploitRef.current !== undefined &&
      appliancExploitRef.current !== undefined &&
      lightingExploitRef.current !== undefined
    )
      return round(
        occupantExploitRef.current +
          appliancExploitRef.current +
          lightingExploitRef.current,
        1,
      )

    return undefined
  }

  const calcExploitableHeatload = (
    scheduleId: string | undefined,
    heatLoad: number | undefined,
    factor: number | undefined,
    exploitRef: React.MutableRefObject<number | undefined>,
  ) => {
    const amount = calculateScheduleSum(scheduleId)
    if (
      heatLoad !== undefined &&
      amount !== undefined &&
      factor !== undefined
    ) {
      exploitRef.current = round((heatLoad * amount * factor) / 1000, 1) // eslint-disable-line
      return exploitRef.current
    }

    return undefined
  }

  const parseNumber = async (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = event.target

    if (value.trim() === '') return 0

    const result = Number(value.replace(/\s/g, '').replace(',', '.'))

    await control.trigger(event.target.name)

    return Number.isNaN(result) ? undefined : result
  }

  useEffect(() => {
    setOccupantLoad(
      Number(
        control.defaultValuesRef.current.parameters?.heatLoad?.occupantHeatLoad,
      ),
    )

    setApplianceLoad(
      Number(
        control.defaultValuesRef.current.parameters?.heatLoad
          ?.applianceHeatLoad,
      ),
    )

    setLightingLoad(
      Number(
        control.defaultValuesRef.current.parameters?.heatLoad?.lightingHeatLoad,
      ),
    )

    setOccupantFactor(
      Number(
        control.defaultValuesRef.current.parameters?.heatLoad
          ?.occupantHeatLoadUtilFactor,
      ),
    )

    setApplianceFactor(
      Number(
        control.defaultValuesRef.current.parameters?.heatLoad
          ?.applianceHeatLoadUtilFactor,
      ),
    )

    setLightingFactor(
      Number(
        control.defaultValuesRef.current.parameters?.heatLoad
          ?.lightingHeatLoadUtilFactor,
      ),
    )
  }, []) // eslint-disable-line

  return (
    <FormGroup>
      <Grid
        container
        item
        alignItems="flex-end"
        spacing={1}
        style={{ marginBottom: '5px' }}
      >
        <Grid item xs={1}>
          <Typography variant="body2">
            {t('energySystem.parameters.heatLoad.heatLoadType')}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" align="center">
            {t('energySystem.parameters.power')} [{powerPerAreaUnit}]
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="body2" align="center">
            {t('energySystem.parameters.heatLoad.schedule')}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" align="center">
            {t('energyType.electricity')} [{energyCapacityUnit}/{areaUnit}]
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" align="center">
            {t('energySystem.parameters.heatLoad.heatLoadUtilizationFactor')}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" align="center">
            {t('energySystem.parameters.heatLoad.usableHeatLoad')} [
            {energyCapacityUnit}/{areaUnit}]
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={1}>
          <Typography>
            {t('energySystem.parameters.heatLoad.occupants')}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.occupantHeatLoad"
            error={errors.parameters?.heatLoad?.occupantHeatLoad}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            onBlur={async (event) => setOccupantLoad(await parseNumber(event))}
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={3}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.occupantScheduleId"
            error={errors.parameters?.heatLoad?.occupantScheduleId}
            rules={validators.required}
            select
            InputProps={centeredSelect}
          >
            {Object.values(schedules).map((schedule) => (
              <MenuItem
                className={classes.centeredSelectList}
                key={schedule.id}
                value={schedule.id}
              >
                {schedule.name}
              </MenuItem>
            ))}
          </ControlledTextField>
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">-</Typography>
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.occupantHeatLoadUtilFactor"
            error={errors.parameters?.heatLoad?.occupantHeatLoadUtilFactor}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            onBlur={async (event) =>
              setOccupantFactor(await parseNumber(event))
            }
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">
            {calcExploitableHeatload(
              occupantScheduleId,
              occupantLoad,
              occupantFactor,
              occupantExploitRef,
            ) ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={1}>
          <Typography>
            {t('energySystem.parameters.heatLoad.appliances')}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.applianceHeatLoad"
            error={errors.parameters?.heatLoad?.applianceHeatLoad}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            onBlur={async (event) => setApplianceLoad(await parseNumber(event))}
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={3}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.applianceScheduleId"
            error={errors.parameters?.heatLoad?.applianceScheduleId}
            rules={validators.required}
            select
            InputProps={centeredSelect}
          >
            {Object.values(schedules).map((schedule) => (
              <MenuItem
                className={classes.centeredSelectList}
                key={schedule.id}
                value={schedule.id}
              >
                {schedule.name}
              </MenuItem>
            ))}
          </ControlledTextField>
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">
            {calcApplianceElectricity() ?? ''}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.applianceHeatLoadUtilFactor"
            error={errors.parameters?.heatLoad?.applianceHeatLoadUtilFactor}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            onBlur={async (event) =>
              setApplianceFactor(await parseNumber(event))
            }
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">
            {calcExploitableHeatload(
              applianceScheduleId,
              applianceLoad,
              applianceFactor,
              appliancExploitRef,
            ) ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={1}>
          <Typography>
            {t('energySystem.parameters.heatLoad.lighting')}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.lightingHeatLoad"
            error={errors.parameters?.heatLoad?.lightingHeatLoad}
            rules={validators.decimal}
            onBlur={async (event) => setLightingLoad(await parseNumber(event))}
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={3}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.lightingScheduleId"
            error={errors.parameters?.heatLoad?.lightingScheduleId}
            rules={validators.required}
            select
            InputProps={centeredSelect}
          >
            {Object.values(schedules).map((schedule) => (
              <MenuItem
                className={classes.centeredSelectList}
                key={schedule.id}
                value={schedule.id}
              >
                {schedule.name}
              </MenuItem>
            ))}
          </ControlledTextField>
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">
            {calcLightningElectricity() ?? ''}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.lightingHeatLoadUtilFactor"
            error={errors.parameters?.heatLoad?.lightingHeatLoadUtilFactor}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            onBlur={async (event) =>
              setLightingFactor(await parseNumber(event))
            }
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">
            {calcExploitableHeatload(
              lightingScheduleId,
              lightingLoad,
              lightingFactor,
              lightingExploitRef,
            ) ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Box my={1}>
        <Divider />
      </Box>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={1}>
          <Typography>{t('common.total')}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">{calcTotalHeatLoad() ?? ''}</Typography>
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={2}>
          <Typography align="center">{calcTotalElectricity() ?? ''}</Typography>
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={2}>
          <Typography align="center">
            {calcTotalExploitableHeatload() ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        alignItems="center"
        spacing={1}
        style={{ marginTop: theme.spacing(2) }}
      >
        <Grid item xs={9}>
          <Typography>
            {t('energySystem.parameters.heatLoad.heatLoadUtilStartTempLabel')}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.heatLoadUtilStartTemp"
            error={errors.parameters?.heatLoad?.heatLoadUtilStartTemp}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            InputProps={{
              margin: 'dense',
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </Grid>
        <Grid item xs={9}>
          <Typography>
            {t(
              'energySystem.parameters.heatLoad.affectingSpaceHeatingStartTempLabel',
            )}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <ControlledTextField
            control={control}
            name="parameters.heatLoad.affectingSpaceHeatingStartTemp"
            error={errors.parameters?.heatLoad?.affectingSpaceHeatingStartTemp}
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            InputProps={{
              margin: 'dense',
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}
